import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import appConstants from '../constants/appConstants';

const AssignIdCard = () => {
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [idCardNumber, setIdCardNumber] = useState('');
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [focusIdCard, setFocusIdCard] = useState(false);
  const [focusAdmission, setFocusAdmission] = useState(false);
  const admissionInputRef = useRef(null);
  const idCardInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
  };

  const handleStudentLookup = async (e) => {
    e.preventDefault();
    if (!admissionNumber.trim()) {
      setError('Please enter an admission number.');
      setStudent(null);
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}student-search`,
        { search: admissionNumber.trim() },
        getAuthHeaders(),
      );

      if (response.data?.direct_match && response.data?.student) {
        setStudent(response.data.student);
        setFocusIdCard(true);
      } else {
        setStudent(null);
        setError(
          'No matching student found. Please try another admission number.',
        );
      }
    } catch (err) {
      setStudent(null);
      setError(
        err?.response?.data?.message || 'Unable to load student right now.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAssignIdCard = async (e) => {
    e.preventDefault();
    if (!student?.id) {
      setError('Please load a student first.');
      return;
    }

    if (!idCardNumber.trim()) {
      setError('Please enter the RFID card number.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await axios.post(
        `${appConstants.API_ROOT_URL}update-student-idcard`,
        {
          id: student.id,
          id_card_no: idCardNumber.trim(),
        },
        getAuthHeaders(),
      );

      if (response.data?.status === 'success' || response.status === 200) {
        setMessage(`ID card number assigned successfully to ${student.name}.`);
        setIdCardNumber('');
        setAdmissionNumber('');
        setStudent(null);
        setFocusAdmission(true);
      } else {
        setError(response.data?.message || 'Unable to assign ID card number.');
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Unable to assign ID card number.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (focusIdCard) {
      idCardInputRef.current?.focus();
      setFocusIdCard(false);
    }
  }, [focusIdCard]);

  useEffect(() => {
    if (focusAdmission) {
      admissionInputRef.current?.focus();
      setFocusAdmission(false);
    }
  }, [focusAdmission]);

  return (
    <Layout>
      <div className="container-fluid py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-11 col-xl-10">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-primary text-white">
                <h4 className="mb-0">Assign ID Card Number</h4>
              </div>
              <div className="card-body">
                <p className="text-muted mb-4">
                  Enter the admission number to load the student, then enter the
                  RFID card number and save it.
                </p>

                <div className="row g-4 align-items-start">
                  <div className="col-12 col-lg-6">
                    <div className="row g-3">
                      <div className="col-12">
                        <form onSubmit={handleStudentLookup}>
                          <label className="form-label fw-bold">
                            Admission Number
                          </label>
                          <div className="input-group">
                            <input
                              ref={admissionInputRef}
                              type="text"
                              className="form-control"
                              value={admissionNumber}
                              onChange={(e) =>
                                setAdmissionNumber(e.target.value)
                              }
                              placeholder="Enter admission number"
                              autoComplete="off"
                            />
                            <button
                              className="btn btn-primary"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? 'Loading...' : 'Load'}
                            </button>
                          </div>
                        </form>
                      </div>

                      <div className="col-12">
                        <form onSubmit={handleAssignIdCard}>
                          <label className="form-label fw-bold">
                            RFID / ID Card Number
                          </label>
                          <div className="input-group">
                            <input
                              ref={idCardInputRef}
                              type="text"
                              className="form-control"
                              value={idCardNumber}
                              onChange={(e) => setIdCardNumber(e.target.value)}
                              placeholder="Enter RFID card number"
                              disabled={!student}
                              autoComplete="off"
                            />
                            <button
                              className="btn btn-success"
                              type="submit"
                              disabled={loading || !student}
                            >
                              {loading ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {message && (
                      <div className="alert alert-success">{message}</div>
                    )}

                    {student && (
                      <div
                        className="border rounded p-3 bg-light"
                        style={{ borderColor: '#0d6efd' }}
                      >
                        <div
                          className="mb-3 fw-bold"
                          style={{
                            fontSize: '1.4rem',
                            color: '#ff4d4f',
                            letterSpacing: '0.02em',
                            textShadow: '0 1px 2px rgba(0,0,0,0.12)',
                          }}
                        >
                          {student.name}
                        </div>
                        <p className="mb-1">
                          <strong>Admission No:</strong> {student.admission_no}
                        </p>
                        <p className="mb-1">
                          <strong>Application No:</strong>{' '}
                          {student.application_no}
                        </p>
                        <p className="mb-0">
                          <strong>Status:</strong> {student.status}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AssignIdCard;
